import React, { useEffect, useRef, useState } from 'react';
import { listenToRideDriverLocation } from '../services/firebase';
import {
  clearMarkers, createMap, destroyMap, drawRoute, fitMapToPoints, forwardGeocode,
  getDirections, placeAssignedAutoMarker, placeMarker,
} from '../services/maps';
import { FINDING_STATUSES, LIVE_STATUSES } from '../utils/intertownTrip';

const isFiniteCoordinate = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
const hasPoint = (place) => isFiniteCoordinate(place?.lat) && isFiniteCoordinate(place?.lng);
const asPoint = (place) => (hasPoint(place) ? { lat: Number(place.lat), lng: Number(place.lng) } : null);
const SHEET_BUCKET_PX = 24; // sheet heights within this many px count as the same fit
const SHEET_SETTLE_MS = 320; // a little longer than the sheet's own slide (.25s)

// The map behind the Intertown trip screen: the pickup and drop pins, the route for the
// current leg, and Anna's auto once her phone is sending a location. It fills whatever
// contains it and draws nothing else: the sheet, the status and every button belong to
// IntertownTripView. Anna's location is read only from the participant-scoped rideLocations
// node (never the fleet's private locations) and handed up through onLocation.
function IntertownLiveMap({
  trip, insets, onLocation, onPanned, recenterRef,
}) {
  const [driverLocation, setDriverLocation] = useState(null);
  const [resolvedDestination, setResolvedDestination] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [userPanned, setUserPanned] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const endpointMarkers = useRef([]);
  const driverMarker = useRef(null);
  const routeLine = useRef(null);
  const routeKey = useRef('');
  const fitKey = useRef('');
  const fittedBucket = useRef(null);
  const userPannedRef = useRef(false);

  const pickup = asPoint(trip?.pickup);
  const storedDestination = asPoint(trip?.destination || trip?.route?.destination);
  const destination = storedDestination || resolvedDestination;
  const status = trip?.status;
  const live = Boolean(trip?.id && LIVE_STATUSES.has(status));
  const top = Math.round(insets?.top ?? 96);
  const bottom = Math.round(insets?.bottom ?? 320);

  // Fixed routes created before exact destination pins were introduced retain
  // their town name. Resolve that once for a useful route overview; custom
  // routes always use their exact stored destination pin.
  useEffect(() => {
    if (storedDestination || !trip?.route?.toName) {
      setResolvedDestination(null);
      return undefined;
    }
    let cancelled = false;
    forwardGeocode(`${trip.route.toName}, Andhra Pradesh, India`, {
      localContext: false,
      restrictToServiceArea: false,
    }).then((place) => {
      if (!cancelled && hasPoint(place)) setResolvedDestination(asPoint(place));
    }).catch(() => {
      if (!cancelled) setResolvedDestination(null);
    });
    return () => { cancelled = true; };
  }, [storedDestination?.lat, storedDestination?.lng, trip?.route?.toName]);

  useEffect(() => {
    if (!live) {
      setDriverLocation(null);
      return undefined;
    }
    return listenToRideDriverLocation(trip.id, (location) => setDriverLocation(location));
  }, [live, trip?.id]);

  useEffect(() => { onLocation?.(driverLocation); }, [driverLocation, onLocation]);
  useEffect(() => { onPanned?.(userPanned); }, [userPanned, onPanned]);

  useEffect(() => {
    if (!trip?.id || !mapRef.current || mapInstance.current || !pickup) return undefined;
    let cancelled = false;
    createMap(mapRef.current, pickup, { showServiceArea: false, zoom: 13, allowNative: true }).then((map) => {
      if (cancelled) { destroyMap(map); return; }
      mapInstance.current = map;
      map.addListener('dragstart', () => {
        userPannedRef.current = true;
        setUserPanned(true);
      });
      setMapReady(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [trip?.id, pickup?.lat, pickup?.lng]);

  useEffect(() => () => {
    clearMarkers(endpointMarkers.current);
    try { driverMarker.current?.setMap(null); } catch { /* noop */ }
    try { routeLine.current?.setMap(null); } catch { /* noop */ }
    // A native map lives under the WebView: release it with the screen.
    destroyMap(mapInstance.current);
    mapInstance.current = null;
  }, []);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !mapReady) return undefined;
    let cancelled = false;
    clearMarkers(endpointMarkers.current);
    endpointMarkers.current = [];
    (async () => {
      if (pickup) {
        const marker = await placeMarker(map, pickup, 'A');
        if (!cancelled) endpointMarkers.current.push(marker);
      }
      if (destination) {
        const marker = await placeMarker(map, destination, 'B');
        if (!cancelled) endpointMarkers.current.push(marker);
      }
    })().catch(() => {});
    return () => { cancelled = true; };
  }, [pickup?.lat, pickup?.lng, destination?.lat, destination?.lng, mapReady]);

  useEffect(() => {
    const map = mapInstance.current;
    const point = asPoint(driverLocation);
    if (!map || !mapReady || !point || !live) {
      try { driverMarker.current?.setMap(null); } catch { /* noop */ }
      driverMarker.current = null;
      return undefined;
    }
    if (driverMarker.current) {
      driverMarker.current.setPosition(point);
    } else {
      let cancelled = false;
      placeAssignedAutoMarker(map, point).then((marker) => {
        if (cancelled) marker.setMap(null);
        else driverMarker.current = marker;
      }).catch(() => {});
      return () => { cancelled = true; };
    }
    return undefined;
  }, [driverLocation?.lat, driverLocation?.lng, live, mapReady]);

  // The route for the leg in progress: the trip itself while searching, Anna to the pickup
  // while she comes, the trip once she has arrived or started, and back to the pickup on a
  // return. Parked at the destination there is nothing to draw.
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !mapReady || !pickup) return undefined;
    const driver = asPoint(driverLocation);
    const finding = FINDING_STATUSES.includes(status);
    const atDestination = status === 'at_destination';
    let target = pickup;
    if (finding || status === 'started' || status === 'arrived_at_pickup') target = destination;
    let origin = driver || pickup;
    if (finding) origin = pickup;
    else if (status === 'confirmed') origin = driver;
    const key = `${status}|${target?.lat || ''},${target?.lng || ''}|${Boolean(driver)}`;

    if (atDestination || !origin || !target) {
      try { routeLine.current?.setMap(null); } catch { /* noop */ }
      routeLine.current = null;
      routeKey.current = key;
      return undefined;
    }
    if (routeKey.current === key) return undefined;
    routeKey.current = key;
    let cancelled = false;
    getDirections(origin, target).then(async (info) => {
      if (cancelled || !info?.polyline) return;
      try { routeLine.current?.setMap(null); } catch { /* noop */ }
      const line = await drawRoute(map, info.polyline);
      if (cancelled) line?.setMap(null);
      else routeLine.current = line;
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [status, pickup?.lat, pickup?.lng, destination?.lat, destination?.lng, Boolean(driverLocation?.lat), mapReady]);

  const recenter = () => {
    const map = mapInstance.current;
    if (!map) return;
    fittedBucket.current = Math.round(bottom / SHEET_BUCKET_PX);
    const driver = asPoint(driverLocation);
    let points = [driver, pickup, destination];
    if (FINDING_STATUSES.includes(status)) points = [pickup, destination];
    else if (status === 'confirmed' || status === 'arrived_at_pickup' || status === 'returning') points = [driver, pickup];
    else if (status === 'started') points = [driver, destination];
    else if (status === 'at_destination') points = [driver || destination];
    const near = status === 'confirmed' || status === 'arrived_at_pickup';
    fitMapToPoints(map, points.filter(Boolean), { top, bottom, left: 28, right: 28 }, near ? 15.5 : 13.5);
    userPannedRef.current = false;
    setUserPanned(false);
  };
  const latestRecenter = useRef(recenter);
  latestRecenter.current = recenter;
  if (recenterRef) recenterRef.current = recenter;

  // Fit again when the leg changes or Anna's first location arrives, unless the rider has moved
  // the map themselves.
  useEffect(() => {
    if (!mapReady || userPannedRef.current) return;
    const driver = asPoint(driverLocation);
    const key = `${status}|${Boolean(driver)}|${destination?.lat || ''},${destination?.lng || ''}`;
    if (fitKey.current === key) return;
    fitKey.current = key;
    recenter();
  }, [status, driverLocation?.lat, driverLocation?.lng, destination?.lat, destination?.lng, mapReady]);

  // And when the sheet opens or closes, so the pins stay in the part of the map that is still
  // visible. The sheet slides for a moment and its height changes on every frame, so wait until it
  // has settled instead of chasing it with a new camera move each time.
  const sheetBucket = Math.round(bottom / SHEET_BUCKET_PX);
  useEffect(() => {
    if (!mapReady || userPannedRef.current || fittedBucket.current === sheetBucket) return undefined;
    const timer = window.setTimeout(() => {
      if (!userPannedRef.current) latestRecenter.current();
    }, SHEET_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [sheetBucket, mapReady]);

  return <div ref={mapRef} className="itt-map" />;
}

// The screen around it ticks every second; the map only needs to hear about a new trip, insets or location.
export default React.memo(IntertownLiveMap);
